/**
 * Entry point. Reads `?flow=…` from the URL, validates the partner
 * origin, and dispatches to the matching flow module. Anything that
 * doesn't add up (missing flow, untrusted origin, no opener) renders
 * a generic "this page must be opened by the Horus SDK" message and
 * does NOT post any messages anywhere.
 */

import { readContextFromUrl } from './postmessage';
import { renderGoogleFlow } from './flows/google';
import { renderEmailPasswordFlow } from './flows/email-password';
import { renderEmailLinkFlow } from './flows/email-link';
import { renderPhoneFlow } from './flows/phone';
import { renderRevealFlow } from './flows/reveal';
import { renderUnifiedFlow } from './flows/unified';
import { el, mount, setBranding } from './util';
import { HorusAuthFlow } from './protocol';
import {
  readBrandingFromUrl,
  applyBranding,
  fetchAppPolicy,
  enforceWhiteLabelTier,
} from './branding';

// Backend base URL — `auth.horuswallet.com` calls this host to fetch
// per-app policy (`/apps/{appId}/policy`) for the white-label tier.
// Default points at the dev stage today because that's the only one
// deployed; once prod (`api.horuswallet.com`) is live, set the default
// to that AND override via `VITE_API_BASE_URL=https://dev-api.horuswallet.com`
// in `.env.local` for dev builds.
const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? 'https://dev-api.horuswallet.com';

// Apply per-popup branding before any flow renders. Partners pass
// branding via SDK config; the SDK serializes it to URL params; we
// read + validate + apply the safe subset (CSS variables + title).
//
// The "hide Powered by Horus" toggle is gated server-side: the auth
// page fetches `/apps/{appId}/policy` and only honors `b_poweredby=0`
// when the app's `whiteLabel` flag is true. The fetch happens in
// parallel with the rest of the page load; if it fails or times out
// we degrade to the conservative default (footer shown).
const rawBranding = readBrandingFromUrl();
applyBranding(rawBranding);
const appId = new URLSearchParams(window.location.search).get('appKey');
const policyPromise = appId
  ? fetchAppPolicy(appId, API_BASE_URL)
  : Promise.resolve({ whiteLabel: false, allowedOrigins: [] });
// Block the flow render briefly on the policy fetch — the response is
// cached at the edge for 5 min so repeat loads are ~free, and the
// blocking window is bounded by the fetch's natural timeout. The
// enforced branding is published to util.ts so every subsequent
// mount() call wraps the flow content with the right footer.
//
// Awaiting here also means the origin allow-list is known before any flow
// starts, so there is no window in which a sign-in could complete and post
// a refresh token before we knew whether the origin was entitled to one.
const policy = await policyPromise;
const branding = enforceWhiteLabelTier(rawBranding, policy);
setBranding(branding);

// Note the asymmetry: no `appKey` means no policy, which means no
// registered origins, which means no refresh token. Rendering and the ID
// token are unaffected, so an older SDK that omits `appKey` keeps working
// exactly as it does today.
const ctx = readContextFromUrl(policy.allowedOrigins);
const flow = new URLSearchParams(window.location.search).get('flow') as HorusAuthFlow | null;

if (!ctx) {
  // Refuse to render anything actionable if we can't validate the
  // requesting origin. No postMessage is sent — by definition we
  // don't know where to send it.
  mount(
    el('div', {}, [
      el('h1', {}, ['Horus Sign-In']),
      el('p', { class: 'subtitle' }, [
        'This page must be opened by the Horus SDK from a permitted partner site.',
      ]),
    ])
  );
} else if (!flow) {
  mount(
    el('div', {}, [
      el('h1', {}, ['Sign in']),
      el('p', { class: 'subtitle' }, ['Missing `flow` parameter.']),
    ])
  );
} else {
  switch (flow) {
    case 'google':
      void renderGoogleFlow(ctx);
      break;
    case 'email_password':
      renderEmailPasswordFlow(ctx);
      break;
    case 'email_link':
      void renderEmailLinkFlow(ctx);
      break;
    case 'phone':
      renderPhoneFlow(ctx);
      break;
    case 'reveal':
      renderRevealFlow(ctx);
      break;
    case 'unified':
      renderUnifiedFlow(ctx);
      break;
    default:
      mount(
        el('div', {}, [
          el('h1', {}, ['Sign in']),
          el('p', { class: 'subtitle' }, [`Unsupported flow: ${flow}`]),
        ])
      );
  }
}
